/*
 * Page bilan (conservation nulle) : le calcul du score, la generation du
 * PDF et le chiffrement se font uniquement dans le navigateur. Les sections
 * visuelles sont decoupees en sous composants dans components/bilan/.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmailDialog } from "@/components/bilan/EmailDialog";
import { SessionMissingView, BilanLoadingView } from "@/components/bilan/BilanEmptyStates";
import { ProfileScoreCard } from "@/components/bilan/ProfileScoreCard";
import { RadarSection } from "@/components/bilan/RadarSection";
import { ForcesPrioritiesSection } from "@/components/bilan/ForcesPrioritiesSection";
import { ActionsSection } from "@/components/bilan/ActionsSection";
import { TransitionSection } from "@/components/bilan/TransitionSection";
import {
  SessionWarningAlert, MedicalCautionAlert, VariantNoteAlert, DisclaimerAlert,
} from "@/components/bilan/BilanAlerts";
import { fetchQuizDefinition, fetchPublicSettings, trackEvent } from "@/lib/api";
import { loadQuizState, loadPrenom, clearAllSessionData } from "@/lib/quizStorage";
import { buildResult } from "@/lib/scoringEngine";
import { downloadPdf } from "@/lib/pdfBilan";
import { makePdfBytes } from "@/lib/bilanActions";
import { devWarn } from "@/lib/logger";

export default function BilanPage() {
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [prenom, setPrenom] = useState(loadPrenom());
  const [sessionMissing, setSessionMissing] = useState(false);

  const saved = useMemo(() => loadQuizState(), []);
  const quizCompleted = Boolean(saved && saved.completed && saved.answers && Object.keys(saved.answers).length > 0);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!quizCompleted) {
      setSessionMissing(true);
      return;
    }
    trackEvent("synthesis_viewed");
    trackEvent("results_viewed");
    fetchQuizDefinition().then(setDefinition).catch(() => setDefinition({ skip_rules: null }));
    fetchPublicSettings()
      .then((s) => { setSettings(s); setSettingsLoaded(true); })
      .catch(() => { setSettings(null); setSettingsLoaded(true); });
  }, [quizCompleted]);

  // Calcul LOCAL du bilan : les réponses ne quittent jamais la session.
  const result = useMemo(() => {
    if (!quizCompleted || !definition || !settingsLoaded) return null;
    try {
      return buildResult(saved.answers, {
        actionsLibrary: settings?.actions_library || null,
        scoringConfig: settings?.scoring_config || null,
        skipRules: definition.skip_rules || undefined,
      });
    } catch (error) {
      // Journalisation controlee : jamais les reponses ni les scores.
      devWarn("[cycliq] calcul local du bilan impossible :", error?.name || "erreur inconnue");
      return null;
    }
  }, [quizCompleted, saved, definition, settings, settingsLoaded]);

  if (sessionMissing) return <SessionMissingView />;
  if (!result) return <BilanLoadingView />;

  const dateStr = new Date(result.created_at).toLocaleDateString("fr-FR", {
    day: "numeric", month: "long", year: "numeric",
  });
  const variant = result.cta_variant || "direct";
  const noCycle = result.flags?.no_cycle;
  const medicalCaution = Boolean(result.flags?.medical_caution);
  const firstAction = (result.actions || [])[0];
  const baremeVersion = result.bareme_version || settings?.bareme_version;

  const goToFit = () => navigate("/fit");

  const handleDownload = async () => {
    try {
      trackEvent("pdf_downloaded");
      const bytes = await makePdfBytes(result, settings, prenom);
      downloadPdf(bytes);
      toast.success("Bilan téléchargé", { description: "Ton PDF a été généré sur ton appareil, sans passage serveur." });
    } catch (error) {
      devWarn("[cycliq] generation locale du PDF impossible :", error?.name || "erreur inconnue");
      toast.error("Téléchargement impossible", { description: "Réessaie dans un instant." });
    }
  };

  const handleClearSession = () => {
    clearAllSessionData();
    toast.success("Session effacée", { description: "Toutes tes données locales ont été supprimées." });
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 max-w-[720px] w-full mx-auto px-4 sm:px-6 pt-8 pb-32">
        {/* En tête */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#235277]">
            Ton bilan CYCLIQ du {dateStr}
          </p>
          <h1 className="font-heading mt-2 text-2xl sm:text-3xl font-semibold tracking-tight text-[#060606]" data-testid="bilan-profile-title">
            {prenom ? `${prenom}, ton profil CYCLIQ : ` : "Ton profil CYCLIQ : "}
            <span className="text-[#235277]">{result.profile.label}</span>
          </h1>
        </motion.div>

        <SessionWarningAlert />

        <ProfileScoreCard result={result} baremeVersion={baremeVersion} />

        {medicalCaution && <MedicalCautionAlert />}

        <RadarSection result={result} baremeVersion={baremeVersion} />

        <ForcesPrioritiesSection forces={result.forces} priorites={result.priorites} />

        <ActionsSection medicalCaution={medicalCaution} actions={result.actions} firstAction={firstAction} />

        {/* Bilan complet : téléchargement local et envoi chiffré */}
        <section className="mt-6 rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-5 sm:p-6 shadow-[var(--shadow-sm)]" data-testid="bilan-pdf-block">
          <h2 className="font-heading text-base font-semibold text-[#060606]">Ton bilan complet, à garder avec toi</h2>
          <p className="mt-1 text-xs leading-relaxed text-[#333333] max-w-[520px]">
            Ton PDF est généré directement dans ton navigateur : synthèse, cinq dimensions
            commentées et plan d’action complet. Si tu choisis l’envoi par email, ton bilan est
            d’abord chiffré sur ton appareil avec un code personnel affiché à l’écran.
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="bilan-download-button"
              onClick={handleDownload}
              className="min-h-[52px] px-6 rounded-xl bg-[#EAFE63] text-[#060606] hover:bg-[#e0f65a] font-heading font-semibold text-base active:scale-[0.98] shadow-[var(--shadow-cta)] transition-colors duration-200"
            >
              <FileDown className="mr-2 w-4 h-4" aria-hidden="true" />
              Télécharger mon bilan
            </Button>
            <EmailDialog
              result={result}
              settings={settings}
              prenomInitial={prenom}
              onPrenomChange={setPrenom}
            />
          </div>
        </section>

        <VariantNoteAlert variant={variant} />

        <DisclaimerAlert noCycle={noCycle} />

        <TransitionSection onStart={goToFit} />

        {/* Effacement de session */}
        <div className="mt-10 text-center">
          <button
            type="button"
            data-testid="bilan-clear-session-button"
            onClick={handleClearSession}
            className="inline-flex items-center gap-2 text-xs text-[#9E9E9E] underline underline-offset-2 hover:text-[#235277] transition-colors duration-150"
          >
            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
            Effacer ma session et mes données locales maintenant
          </button>
        </div>
      </main>

      {/* CTA sticky mobile : transition vers la qualification, jamais d'achat direct */}
      <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-[rgba(249,254,255,0.94)] backdrop-blur-sm border-t border-[rgba(35,82,119,0.12)] px-4 py-3">
        <Button
          data-testid="bilan-sticky-fit-button"
          onClick={goToFit}
          className="w-full min-h-[52px] rounded-xl bg-[#235277] hover:bg-[#1E4767] text-white font-heading font-semibold active:scale-[0.98] transition-colors duration-200"
        >
          Vérifier si CYCLIQ EXCELLENCE me correspond
          <ArrowRight className="ml-2 w-4 h-4" aria-hidden="true" />
        </Button>
      </div>

      <Footer />
    </div>
  );
}
